// Versioned so a corrected recovery strategy can retry once for sessions that
// were trapped by an earlier deployed error boundary. Version 4 also gives
// embedded Safari browsers a second cache-busted document request when their
// opaque “Importing a module script failed” error does not identify the stale
// lazy bundle by filename.
const RECOVERY_KEY_PREFIX = "releafa:stale-bundle-recovery:v4:";
const RECOVERY_ATTEMPT_PARAM = "releafa_recovery";
const MAX_OPAQUE_MODULE_RECOVERY_ATTEMPTS = 2;
const KNOWN_STALE_REFERENCE_SIGNATURES = [
  "cannabisleaficon is not defined",
  "can't find variable: cannabisleaficon",
  "getstatedisplayname is not defined",
  "can't find variable: getstatedisplayname",
];

function getErrorMessage(error: unknown): string {
  return (
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : ""
  ).toLowerCase();
}

export function isLegacyDirectoryDisplayNameError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return message.includes("getstatedisplayname is not defined")
    || message.includes("can't find variable: getstatedisplayname");
}

export function getFreshCurrentPageUrl(recoveryAttempt?: number): string {
  if (typeof window === "undefined") return "/";
  const refreshUrl = new URL(window.location.href);
  refreshUrl.searchParams.set("releafa_refresh", Date.now().toString());
  if (recoveryAttempt && recoveryAttempt > 0) {
    refreshUrl.searchParams.set(RECOVERY_ATTEMPT_PARAM, recoveryAttempt.toString());
  }
  return refreshUrl.toString();
}

export function forceFreshCurrentPage(recoveryAttempt?: number): void {
  if (typeof window === "undefined") return;
  const refreshUrl = getFreshCurrentPageUrl(recoveryAttempt);

  // Assigning a new document is more reliable than replace() in Instagram's
  // in-app browser after a lazy-module request fails. The recovery guard above
  // still prevents an automatic loop if the current bundle cannot load.
  if (typeof window.location.assign === "function") {
    window.location.assign(refreshUrl);
    return;
  }

  window.location.replace(refreshUrl);
}

function getLazyAssetIdentity(error: unknown): string {
  const message = getErrorMessage(error);
  const assetMatch = message.match(/\/assets\/([^\s"')?]+\.(?:js|mjs))/i);

  // Vite hashes lazy bundles. Keying the one-time recovery to the missing asset
  // (rather than only the route) lets a later deployment recover independently.
  return assetMatch?.[1] ?? message.slice(0, 160);
}

function isOpaqueModuleScriptFailure(error: unknown): boolean {
  const message = getErrorMessage(error);
  return message.includes("importing a module script failed") && !message.includes("/assets/");
}

function getRecoveryAttemptFromUrl(): number {
  if (typeof window === "undefined") return 0;
  const rawAttempt = new URL(window.location.href).searchParams.get(RECOVERY_ATTEMPT_PARAM);
  const attempt = Number.parseInt(rawAttempt || "0", 10);
  return Number.isFinite(attempt) && attempt > 0 ? attempt : 0;
}

export function isStaleBundleError(error: unknown): boolean {
  // Only inspect the browser's error message. Every production React stack has
  // `/assets/...` in it, so inspecting stacks (or treating any asset URL as a
  // stale bundle) turns unrelated application errors into a site-wide reload.
  const message = getErrorMessage(error);

  const isLazyModuleFailure = [
    "failed to fetch dynamically imported module",
    "importing a module script failed",
    "error loading dynamically imported module",
    "chunkloaderror",
    "loading chunk",
  ].some((signature) => message.includes(signature));

  // This is a narrowly scoped compatibility path for a previously deployed
  // owner-dashboard chunk. Instagram can retain that old chunk after a new
  // deployment, so reload it once into the repaired bundle. Do not broaden
  // this to generic ReferenceErrors: unrelated page bugs must remain visible.
  const isKnownStaleReference = KNOWN_STALE_REFERENCE_SIGNATURES.some((signature) => message.includes(signature));

  return isLazyModuleFailure || isKnownStaleReference;
}

/**
 * Reload once after a deployment replaces a lazy-loaded Vite bundle.
 * The session guard avoids a reload loop if a separate application error occurs.
 */
export function recoverFromStaleBundle(error: unknown): boolean {
  if (typeof window === "undefined" || !isStaleBundleError(error)) return false;

  const recoveryKey = `${RECOVERY_KEY_PREFIX}${window.location.pathname}:${getLazyAssetIdentity(error)}`;
  const maxAttempts = isOpaqueModuleScriptFailure(error) ? MAX_OPAQUE_MODULE_RECOVERY_ATTEMPTS : 1;
  let recoveryAttempt = getRecoveryAttemptFromUrl();
  try {
    const recordedAttempt = Number.parseInt(window.sessionStorage.getItem(recoveryKey) || "0", 10);
    recoveryAttempt = Math.max(recoveryAttempt, Number.isFinite(recordedAttempt) ? recordedAttempt : 0);
    if (recoveryAttempt >= maxAttempts) return false;
    recoveryAttempt += 1;
    window.sessionStorage.setItem(recoveryKey, recoveryAttempt.toString());
  } catch {
    // Storage can be unavailable in private or embedded browsers. The
    // cache-busting URL carries the bounded retry count in that case, so a
    // failed module cannot trap a visitor in an endless refresh loop.
    if (recoveryAttempt >= maxAttempts) return false;
    recoveryAttempt += 1;
  }

  // Instagram's in-app browser can retain an older document after reload(). A
  // one-time cache-busting URL asks it for the current document and newest
  // entry bundle while preserving the visitor's route and query parameters.
  forceFreshCurrentPage(recoveryAttempt);
  return true;
}

/**
 * A successfully rendered route proves the latest application bundle is usable.
 * Clear recovery guards for that route so a future deployment's lazy asset can
 * receive its own one-time cache-busting recovery instead of being blocked by
 * a stale session flag from an earlier build.
 */
export function clearStaleBundleRecoveryForCurrentPage(): void {
  if (typeof window === "undefined") return;

  const prefix = `${RECOVERY_KEY_PREFIX}${window.location.pathname}:`;
  try {
    const keys = Array.from({ length: window.sessionStorage.length }, (_, index) => window.sessionStorage.key(index))
      .filter((key): key is string => Boolean(key && key.startsWith(prefix)));
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Embedded and private browsers can deny session storage. There is nothing
    // to clear, and the asset-specific recovery key will still work on reload.
  }
}
